/**
 * I18nContext.jsx — i18n provider for the licensor frontend.
 *
 * Mirrors the standalone bundle's behaviour (`manchala-i18n.js`) so what
 * customers see on their drop-in ZIP matches what we render here. Reads from
 * `${API}/i18n/dict?lang=<code>` and caches in
 * localStorage.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { API } from '../lib/api';
const STORAGE_LANG = 'retailcliq-i18n:lang';
const CACHE_TTL_MS = 60 * 60 * 1000;  // 1 hour
const CACHE_PREFIX = 'retailcliq-i18n';
const FALLBACK_LOCALES = [
  { code: 'en-IN', label: 'English',    rtl: false },
  { code: 'hi-IN', label: 'हिन्दी',        rtl: false },
  { code: 'ta-IN', label: 'தமிழ்',         rtl: false },
  { code: 'te-IN', label: 'తెలుగు',        rtl: false },
  { code: 'mr-IN', label: 'मराठी',        rtl: false },
  { code: 'bn-IN', label: 'বাংলা',        rtl: false },
  { code: 'gu-IN', label: 'ગુજરાતી',       rtl: false },
  { code: 'kn-IN', label: 'ಕನ್ನಡ',        rtl: false },
  { code: 'ml-IN', label: 'മലയാളം',     rtl: false },
  { code: 'pa-IN', label: 'ਪੰਜਾਬੀ',         rtl: false },
  { code: 'or-IN', label: 'ଓଡ଼ିଆ',         rtl: false },
  { code: 'ur-IN', label: 'اردو',         rtl: true  },
  { code: 'ar-AE', label: 'العربية',       rtl: true  },
  { code: 'de-DE', label: 'Deutsch',     rtl: false },
  { code: 'fr-FR', label: 'Français',    rtl: false },
  { code: 'it-IT', label: 'Italiano',    rtl: false },
  { code: 'es-ES', label: 'Español',     rtl: false },
  { code: 'el-GR', label: 'Ελληνικά',     rtl: false },
];

const Ctx = createContext({
  t: (k, f) => f || k,
  lang: 'en-IN',
  setLang: () => {},
  locales: FALLBACK_LOCALES,
  ready: false,
  refresh: () => {},
});

function readCache(lang) {
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}:${lang}`);
    if (!raw) return null;
    const j = JSON.parse(raw);
    if (Date.now() - j.fetchedAt > CACHE_TTL_MS) return null;
    return j;
  } catch (e) { return null; }
}
function writeCache(lang, dict, flat) {
  try {
    localStorage.setItem(`${CACHE_PREFIX}:${lang}`, JSON.stringify({ fetchedAt: Date.now(), dict, flat }));
  } catch (e) { /* quota */ }
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem(STORAGE_LANG) || 'en-IN');
  const [locales, setLocales] = useState(FALLBACK_LOCALES);
  const [dict, setDict] = useState({});
  const [flat, setFlat] = useState({});
  const [ready, setReady] = useState(false);

  const applyDir = useCallback((code) => {
    const loc = (locales || FALLBACK_LOCALES).find(l => l.code === code);
    document.documentElement.setAttribute('dir', loc && loc.rtl ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', code);
  }, [locales]);

  const fetchDict = useCallback(async (code, bypassCache = false) => {
    if (!bypassCache) {
      const c = readCache(code);
      if (c) { setDict(c.dict || {}); setFlat(c.flat || {}); return; }
    }
    try {
      const r = await fetch(`${API}/i18n/dict?lang=${encodeURIComponent(code)}`);
      const d = await r.json();
      setDict(d.nested || {});
      setFlat(d.flat || {});
      writeCache(code, d.nested || {}, d.flat || {});
    } catch (e) {
      setDict({}); setFlat({});
    }
  }, []);

  // Boot: set initial dir/lang IMMEDIATELY (no network wait) so the very
  // first render carries the correct direction. Then fetch locales + dict.
  useEffect(() => {
    let cancelled = false;
    // Synchronous: trust FALLBACK_LOCALES until server confirms.
    const initialLoc = FALLBACK_LOCALES.find(l => l.code === lang);
    document.documentElement.setAttribute('dir',  initialLoc && initialLoc.rtl ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', lang);

    (async () => {
      try {
        const r = await fetch(`${API}/i18n/locales`);
        const d = await r.json();
        if (!cancelled && Array.isArray(d.locales) && d.locales.length) setLocales(d.locales);
      } catch (e) { /* fallback */ }
      await fetchDict(lang);
      if (!cancelled) { setReady(true); applyDir(lang); }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, []);

  useEffect(() => { if (ready) applyDir(lang); }, [lang, ready, applyDir]);

  const setLang = useCallback(async (code) => {
    setLangState(code);
    try { localStorage.setItem(STORAGE_LANG, code); } catch (e) {}
    // Flip direction IMMEDIATELY (synchronous) so rapid switches are race-free.
    applyDir(code);
    await fetchDict(code);
  }, [fetchDict, applyDir]);

  const refresh = useCallback(async () => {
    try { localStorage.removeItem(`${CACHE_PREFIX}:${lang}`); } catch (e) {}
    await fetchDict(lang, true);
  }, [lang, fetchDict]);

  const t = useCallback((key, fallback) => {
    if (!key) return fallback || '';
    if (flat[key] != null) return flat[key];
    const parts = String(key).split('.');
    let node = dict;
    for (const p of parts) {
      if (node && typeof node === 'object' && p in node) node = node[p];
      else return fallback != null ? fallback : key;
    }
    return typeof node === 'string' ? node : (fallback != null ? fallback : key);
  }, [dict, flat]);

  return (
    <Ctx.Provider value={{ t, lang, setLang, locales, ready, refresh }}>{children}</Ctx.Provider>
  );
}

export function useI18n() { return useContext(Ctx); }
export function useT()    { return useContext(Ctx).t; }
